import React, { useState, useEffect } from 'react';
import { View, StyleSheet } from 'react-native';
import { DataTable, TextInput } from '../uiold';
import { useMarketSalesContext } from './Context';

type CellProps = {
  initialValue: number | string;
  paymentID?: string;
  vendorID: string;
  allowNegative?: boolean;
  noteCategoryID?: string;
  disabled?: boolean;
};

type HeaderCellProps = {
  children: string;
};

const PARTIAL_NUMBER_REGEX = /^-?\d*\.?\d*$/;

// This will validate as the user is typing so we allow partial numbers while
// waiting for more characters to be entered.
function isPartialNumber(text: string, allowNegative = false) {
  if (!PARTIAL_NUMBER_REGEX.test(text)) {
    return false;
  }
  if (!allowNegative && text.charAt(0) === '-') {
    return false;
  }
  return true;
}

let InputCell: React.FC<CellProps> = React.memo((props: CellProps) => {
  let { vendorID, paymentID, initialValue, noteCategoryID, disabled } = props;
  let { onPlacementCellBlur } = useMarketSalesContext();
  let [value, setValue] = useState(String(initialValue));

  // Re-set the state when the initial value changed.
  useEffect(() => {
    setValue(String(initialValue));
  }, [initialValue]);

  let onChangeText = (text: string) => {
    if (
      noteCategoryID ||
      text === '' ||
      (vendorID && isPartialNumber(text, props.allowNegative))
    ) {
      setValue(text);
    }
  };

  let onBlur = () => {
    // Do nothing when value doesn't change
    if (value === String(initialValue)) {
      return;
    }
    if (noteCategoryID) {
      onPlacementCellBlur({
        vendorID,
        noteCategoryID,
        value: value,
      });
    } else {
      // TODO: We should probably round to some number of decimal places. It
      // depends on the currency but in most cases 2 decimal places.
      let numericValue = Number(value) || 0;
      let textValue = value ? numericValue.toString() : '';
      onPlacementCellBlur({
        vendorID,
        paymentID,
        value: numericValue,
      });
      setValue(textValue || '0');
    }
  };

  return (
    <View style={styles.root}>
      <TextInput
        value={value}
        onBlur={onBlur}
        onChangeText={onChangeText}
        style={styles.input}
        disabled={disabled}
      />
    </View>
  );
});

function InputHeaderCell(props: HeaderCellProps) {
  return <DataTable.HeaderCell style={styles.root} {...props} />;
}

let styles = StyleSheet.create({
  root: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    maxWidth: 80,
  },
  input: {
    height: 28,
    maxWidth: 80,
    fontSize: 14,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    textAlign: 'right',
  },
});

export { InputCell, InputHeaderCell };
